import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {
    AjaxDiscussionRestControllerApi,
    AjaxGroupRestControllerApi,
    AjaxSeriesRestControllerApi,
    DiscussionWithoutContentDTO,
} from "@/generated-client";
import {ClientFactory} from "@/main/common/ClientFactory";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import {MetaHandler} from "@/main/common/MetaHandler";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import {showAddDiscussionToSeriesDialog} from "@/main/react/Dialog/AddDiscussionToSeriesDialog";
import {isBookmarksSeries} from "@/main/react/series/BookmarksSeriesLocalization";

import {
    rememberViewerActionRecord,
    resolveViewerActionRecord,
    useViewerActionRecord,
} from "@/main/common/ViewerActionRecords";
import {buildUserSafetyDropdownItems, useUserSafetyActions} from "@/main/react/user/useUserSafetyActions";
import {Localizer} from "@/main/react/Types";
import type {FloatingActionGroup} from "@/main/react/floating-actions/FloatingActionConfig";
import {useFloatingActionGroup} from "@/main/react/floating-actions/FloatingActionContributionStore";
import {useIsMobileViewport} from "@/main/react/hooks/useIsMobileViewport";
import {toast} from "sonner";
import {getSafeToastMessageFromError} from "@/main/common/ApiUtils";
import ReviewFacetDialog from "@/main/react/discussion/facet/ReviewFacetDialog";
import type {QuestionState, QuestionStateView} from "@/main/common/QuestionFacetTypes";
import type {LocalizationKey} from "@/main/util/LocalizationUtil";

import {
    ArrowLeft,
    ArrowRight,
    Archive,
    Bell,
    BellOff,
    Bookmark,
    BookMinus,
    BookPlus,
    ChartArea,
    Check,
    Edit3,
    Flag,
    Link,
    List,
    ListPlus,
    Mail,
    MessageSquareText,
    MoreHorizontal,
    Pin,
    RotateCcw,
    Share,
    Share2,
    Star,
    Trash2,
    ArrowBigDown,
    ArrowBigUp,
    X,
    UserPlus2,
} from "lucide-react";
import {CtaButton, CtaButtonDropdown, CtaToggle, DropdownItem, DropdownSection, VoteContainer} from "@/main/react/buttons/CtaButton";
import {getCtaToneClass} from "@/main/react/buttons/cta/ctaShared";
import DiscussionMobileActionBar, {type DiscussionMobileSectionNavigation} from "@/main/react/discussion/DiscussionMobileActionBar";
import {copyUrlToClipboard, shareUrl} from "@/main/util/PlatformIdentifierUtil";
import {showExcludeDiscussionDialog} from "@/main/react/Dialog/GroupExcludeDiscussionDialog";
import {showReportEntityDialog} from "@/main/react/Dialog/ReportEntityDialog";
import {showAskToAnswerDialog} from "@/main/react/Dialog/AskToAnswerDialog";
import {SendPostToNewsletterDialog, useSendPostToNewsletter} from "@/main/react/newsletter/SendPostToNewsletterDialog";
import {showThoughtTextEditorDialog} from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import {showYesNoDialog} from "@/main/react/Dialog/YesNoDialog";
import {ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface} from "@/main/react/account_access/AccountAccessDialogContext";
import {ButtonGroup} from "@/components/ui/button-group";

import {
    emitDiscussionAddedToSeries,
    emitDiscussionRemovedFromSeries,
    emitDiscussionViewerAgreementChanged,
    emitSeriesAddedToDiscussion,
    emitSeriesRemovedFromDiscussion,
    onSeriesAddedToDiscussion,
    onSeriesRemovedFromDiscussion,
} from "@/main/common/EventRegistry";
import logger from "@/lib/logger";
import {trackBookmarkAdded, trackBookmarkRemoved, trackDiscussionVote, trackShare, trackThoughtComposerOpened} from "@/main/analytics/analyticsEvents";
import {
    agreementFromVoteState,
    applyVoteToState,
    DiscussionVoteState,
    parseDiscussionVoteState,
} from "@/main/common/DiscussionVoteState";
import {classifyDiscussionVoteFailure, logDiscussionVoteFailure} from "@/main/common/DiscussionVoteTelemetry";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { stashPendingQuote } from "@/main/react/editor/discussion-embed/pendingQuote";
import { CREATE_NEW_DISCUSSION_PATH } from "@/main/navigation/composerRoutes";
import { useDiscussionFollow } from "@/main/react/discussion/useDiscussionFollow";
import { useDiscussionNotificationMute } from "@/main/react/discussion/useDiscussionNotificationMute";

type Props = {
    /**
     * `"row"` is the feed row's action row (SV-135 S3, design §5.3): it spreads under the text column
     * instead of hugging the right edge, every content action rests muted (`getCtaToneClass`), and the
     * `…` menu is omitted because the feed row renders it on its author line (D2, slice S2). Every
     * `data-playwright-id` and the reply, vote, share and save order are unchanged. Defaults to
     * `"default"`, which the discussion page keeps in phase 1.
     */
    layout?: "default" | "row";
    /**
     * SV-135 D2 (round 31 `3b`): where the `…` menu renders. When the ref holds an element, the menu is
     * PORTALED into it, in any layout, so a host can place it (the feed row puts it at the top right of its
     * author line) while its sections stay built here, in one place. When no ref is passed, the default
     * layout keeps the menu at the end of its row and `layout="row"` omits it. The host owns the element
     * and must raise it above any whole-row link.
     *
     * <p>A REF, not an element: this component reads it in a layout effect and keeps the element in its
     * own state, so the menu lands before the first paint and only this component re-renders for it. An
     * element prop would need the host to hold it in state, re-rendering the whole card (media carousel
     * included) once per mount on every feed page.
     */
    menuSlot?: React.RefObject<HTMLElement | null>;
    discussionPublicId: string;
    discussionSlug?: string;
    initialAgreementsCount: number;
    initialDisagreementsCount: number;
    initialAddedToSeriesCount: number;
    initialArgumentsCount?: number;
    // Stance titles for the inline "write an argument" editor opened from the argument-count button.
    // Optional: discussion cards in feeds don't carry custom titles, so we fall back to the generic
    // agree/disagree labels in that case.
    agreementAgreementTitle?: string;
    disagreementAgreementTitle?: string;
    // The post's own title and author, for the native share payload. Optional so every existing mount
    // (including `ComponentPage`) keeps compiling; when absent the share sheet degrades to exactly its
    // previous shape rather than composing a half-empty sentence.
    discussionTitle?: string;
    authorDisplayName?: string;
    // The post author's USERNAME, for the overflow menu's block / mute pair (design 2026-08-10, slice A).
    // Deliberately separate from `authorDisplayName` above, which is a display string bound for the share
    // payload and may be the localized stand-in for a deleted account — a safety action must address the
    // real account or address nobody. Optional, and a blank one offers no controls rather than the wrong ones.
    authorUsername?: string;
    isPrivateDiscussion: boolean;
    initiallyAgreeing: boolean;
    initiallyDisagreeing: boolean;
    initiallyAddedToAnySeries: boolean;
    initiallySavedToBookmarks?: boolean;
    isArchived?: boolean;
    isDeleted?: boolean;
    localizer: Localizer;
    canEdit: boolean;

    // Group / exclusion related (optional)
    initiallyExcluded?: boolean;
    excludeReason?: string;
    excludedByUserId?: number | null;
    excludedAt?: string | null;
    groupPublicId?: string;
    initiallyViewerIsLeader?: boolean;
    initiallyPinnedInGroup?: boolean;
    initialPinnedDiscussionPinId?: number | null;
    onPinnedInGroup?: (discussion: DiscussionWithoutContentDTO & { pinId?: number; id?: number }) => void;
    onUnpinnedFromGroup?: (discussionPublicId?: string, pinId?: number | null) => void;

    discussionIsInGroup?: boolean;
    showManageActions?: boolean;
    /**
     * Whether the viewer is an audience for this post (`DASHBOARD-25`). Default `true` — every surface
     * that does not pass it is unchanged, which is what lets this land before any caller exists.
     *
     * <p>Pass `false` on a list of the viewer's OWN work. On `/my_dashboard/discussions/authored` the
     * footer today offers to upvote, downvote and bookmark writing the viewer produced: four of six
     * slots spent on reader actions nobody can meaningfully take on themselves, while the reasons to
     * be on that page sit behind the `…`.
     *
     * <p><b>One prop, not four.</b> These are one decision — "this is my own work, I am not an audience
     * for it" — and four booleans would be four ways for a caller to be half right.
     *
     * <p><b>The counts stay.</b> Suppression turns the vote pair into the same static count display a
     * deleted post already uses; an author very much wants to see how many upvotes a post has, they
     * just cannot cast one. Share is deliberately NOT suppressed: promoting your own writing is an
     * author action, not a reader one.
     *
     * <p>Sibling of `DiscussionCardDisplayer.showReadCount` (`f23d6302a`), driven by the same
     * `isOwnWorkSubsection` predicate that already sets `showAuthor={!isOwnWorkSubsection}`. Extending
     * that family, not opening a new one.
     */
    showViewerReactions?: boolean;
    /**
     * This card is rendered on the viewer's OWN saved list (`/my_dashboard/discussions/saved`).
     *
     * <p><b>One prop, not two</b>, for the same reason `showViewerReactions` above is one and not four:
     * these are one fact about the surface — "this list IS the member's saved collection, so the
     * bookmark is destructive here and organising the collection is the job" — and two booleans would
     * be two ways for a caller to be half right. Two rows ride it:
     *
     * <ul>
     *   <li><b>`EMPTY-STATES-81`</b> — the unsave toast gains an <b>Undo</b>. On this surface the filled
     *       bookmark removes the row the member is looking at, and the host filters the id out of the
     *       rendered page at once, so a mis-tap costs the item outright and there is nothing left to
     *       re-tap. Everywhere else the same control destroys nothing in view, which is exactly why this
     *       is a surface treatment and NOT a change to the toggle.</li>
     *   <li><b>`EMPTY-STATES-100`</b> — <b>Add to series</b> is promoted out of the `⋯` menu into the
     *       footer as a named verb (wave-14 Q2: own-work collections get one primary verb, and the
     *       sub-tab's own verb comes out of the overflow). Saved IS stored as a series, so promoting a
     *       post out of that one unnamed bucket into a named one is the organising move this route never
     *       offered visibly. Promoted, never duplicated: the menu item is withheld under the same
     *       predicate, so the action exists exactly once at any width.</li>
     * </ul>
     *
     * <p>Default `false`, so the feed, the discussion page, search, groups and series are byte-identical.
     */
    viewerSavedList?: boolean;
    // When true (only the full discussion page passes this — feed cards must not), also render the
    // mobile-only bottom action bar mirroring these engagement actions. See DiscussionMobileActionBar.
    showFloatingMobileBar?: boolean;
    /**
     * `DISCUSSION-26` / `DISCUSSION-41` (wave-12 sheet Q4, option A). The discussion page's section list
     * and its selection handler, forwarded to the mobile bar.
     *
     * <p>Threaded through this component rather than rendered beside it because the bar is portaled from
     * HERE and nowhere else, and because its presence is what makes the bar's count button a NAVIGATION
     * control: without a section handler the count has nowhere to go and keeps opening the composer,
     * which is the behaviour every non-page caller still wants. One prop, one predicate, both halves.
     */
    mobileSectionNavigation?: DiscussionMobileSectionNavigation;
    /**
     * End-of-post band (slice `1f`, `designs/00-inbox/2026-07-30-end-of-post-continuation-cluster-ux-design.md`
     * §0). One portal target the discussion page's content displayer places directly after ITS footer row:
     * `replyRow`. (A `heading` anchor carrying "Was this worth your time?" over the vote row shipped with it
     * and was removed on 2026-09-14: no text of ours goes between a member's post and its controls.)
     *
     * <p>Rendered from THIS component's state, the construction `DiscussionMobileActionBar` uses, so the band
     * owns nothing: the reply row is a door into the same composer the count button opens. It is never a
     * second vote control (two mounted vote pairs agree at mount and silently diverge on the first click,
     * §3.1), and it reads no store.
     *
     * <p>Absent (every feed card, `ComponentPage`), nothing renders.
     */
    endOfPostAnchors?: { replyRow: HTMLElement | null };
    /**
     * SV-141 D1 (slice `1b`): this bar sits on a SHORT post's page (`isShortPostPage` in `DiscussionPage`).
     * A short post has no thoughts: the server refuses one under a `title IS NULL` post
     * (`ThoughtService`), so every door into the thought composer is withheld rather than offered and
     * refused. That is the thoughts count button, the end-of-post reply row, the desktop corner "Share a
     * thought" action and the mobile bar's comment button. Votes, share, bookmark and the `…` menu stay.
     *
     * <p>Default `false`, so every other caller (feed cards, long posts, `ComponentPage`) is unchanged.
     */
    isShortPost?: boolean;
    /**
     * QUESTION facet (design §11.1). Two effects: the reply CTA reads "Write an answer", and the post's
     * own menu gains the mark-answered / reopen pair.
     *
     * Note there is deliberately NO verb offered in `ANSWERED`. That state exists only because an answer
     * is accepted, `reopen` is refused by the server while one is (`unaccept_first`), and chaining an
     * unaccept behind the user's back is explicitly forbidden by ClientFactory's contract — so the exit
     * from `ANSWERED` is Unaccept on the answer card, which is where the state came from. Offering a
     * control whose only outcome is a refusal is worse than offering none.
     */
    isQuestion?: boolean;
    questionState?: QuestionState;
    onQuestionStateChanged?: (view: QuestionStateView) => void;
    /**
     * SV-130 "Ask someone to answer" eligibility, VIEWER-specific and computed server-side (round-29 D12,
     * A19): true for the question's author unless they are in the asker holdout. Read together with
     * `questionState === "OPEN"` and fail-CLOSED, so a held-out asker never sees an item that refuses them.
     */
    askToAnswerAvailable?: boolean;
    /**
     * REVIEW facet (§5.7). The author's own menu gains *Add review details* / *Edit review details*.
     *
     * <p>Offered on a post that has no facet as well as one that does, which is the whole point of the
     * path: without it the FIRST reviews of anything are permanently unfacetable, and those are exactly
     * the posts a newly-created subject most wants to aggregate.
     */
    reviewSubjectPublicId?: string;
    reviewSubjectName?: string;
    reviewRatingHalfStars?: number;
    onReviewFacetChanged?: (next: { subjectName?: string; ratingHalfStars?: number }) => void;
    seriesNavigation?: {
        backToSeriesHref: string;
        backToSeriesTitle?: string;
        previous?: {
            href: string;
            title?: string;
        };
        next?: {
            href: string;
            title?: string;
        };
    };
};

/** Scope id under which the full discussion page owns the bottom-right floating-action corner. */
const DISCUSSION_FLOATING_SCOPE = "discussion-view";

export default function DiscussionButtons(props: Props) {
    const isMobileViewport = useIsMobileViewport();
    const isUserLoggedIn = MetaHandler.isUserLoggedIn();
    // `DASHBOARD-25`. Defaulted here rather than in the destructure so every existing call site keeps
    // today's behaviour untouched.
    const showViewerReactions = props.showViewerReactions !== false;

    const apiClient: AjaxDiscussionRestControllerApi = useMemo(() => ClientFactory.getAjaxDiscussionRestControllerApi(), []);
    const groupApi: AjaxGroupRestControllerApi = useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);
    const bookmarksApi: AjaxSeriesRestControllerApi = useMemo(() => ClientFactory.getAjaxSeriesRestControllerApi(), []);

    // The props describe the DTO this bar was rendered from, and a DTO can be OLDER than the viewer's
    // own vote — a Back-restored feed item is the case this exists for, since the restoration cache
    // banks the pre-vote items and outranks the fresh server seed. Design §4d.
    const seedVoteState = (): DiscussionVoteState => resolveViewerActionRecord("discussion-vote", props.discussionPublicId, {
        agreementsCount: props.initialAgreementsCount,
        disagreementsCount: props.initialDisagreementsCount,
        viewerAgreeing: props.initiallyAgreeing,
        viewerDisagreeing: props.initiallyDisagreeing,
    });

    const [agreementsCount, setAgreementsCount] = useState(() => seedVoteState().agreementsCount);
    const [disagreementsCount, setDisagreementsCount] = useState(() => seedVoteState().disagreementsCount);

    const [isAgreeing, setIsAgreeing] = useState(() => seedVoteState().viewerAgreeing);
    const [isDisagreeing, setIsDisagreeing] = useState(() => seedVoteState().viewerDisagreeing);
    const [isTogglingAgreement, setIsTogglingAgreement] = useState(false);
    // Exclusion and pinning are properties of a (group, discussion) PAIR, not of the discussion — the
    // same post can be pinned in one group and not another, so the record id has to carry both. A
    // discussion rendered outside a group has no id here, which correctly disables the record for it.
    const groupScopedResourceId = props.groupPublicId && props.discussionPublicId
        ? `${props.groupPublicId}:${props.discussionPublicId}`
        : undefined;
    // Every one of these is a viewer ACTION whose only other record is a DTO that can go stale — see
    // `ViewerActionRecords` for the three ways it does. The hook seeds from the record, writes through
    // on set, and resyncs to `record ?? prop`, so the props below stay the fallback they always were.
    const [isSavedToBookmarks, setIsSavedToBookmarks] = useViewerActionRecord(
        "discussion-bookmark", props.discussionPublicId, props.initiallySavedToBookmarks ?? false,
    );

    const [isExcluded, setIsExcluded] = useViewerActionRecord(
        "discussion-group-exclusion", groupScopedResourceId, props.initiallyExcluded ?? false,
    );
    const [busy, setBusy] = useState(false);
    const [savingToBookmarks, setSavingToBookmarks] = useState(false);
    // The in-flight guard for the bookmark write, held in a ref as well as in state. The state drives
    // the button's disabled/loading look; the ref is what `setBookmarksSaved` reads, because a caller
    // that fires from a captured closure (the undo toast) would otherwise see the value from the render
    // that created it. See `setBookmarksSaved`.
    const savingToBookmarksRef = React.useRef(false);
    // `DISCUSSION-58` — the share path's in-flight guard, in the same shape as the bookmark one above
    // and for the same reason: the state drives the trigger's pending look, the ref is what the handler
    // reads, because a second tap arrives through a closure the first render created.
    //
    // Share was wired fire-and-forget with no pending state, and on the fallback path it awaits a
    // dynamic import of the `share-drawer` chunk before anything appears on screen. A member who taps
    // again in that gap ran the whole handler a second time: `trackShare` fired twice and the drawer's
    // re-entrancy probe raced React's first commit. This removes the second tap rather than handling it.
    const [sharePending, setSharePending] = useState(false);
    const sharePendingRef = React.useRef(false);
    const [isArchived, setIsArchived] = useViewerActionRecord(
        "discussion-archived", props.discussionPublicId, props.isArchived ?? false,
    );
    const [archiveSubmitting, setArchiveSubmitting] = useState(false);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    // The pin is one record, not two: a `pinned` with no `pinId` cannot be undone, and holding them
    // apart is how they drift. `useMemo` on the seed because it is an OBJECT in the hook's deps.
    const initialGroupPin = useMemo(() => ({
        pinned: Boolean(props.initiallyPinnedInGroup),
        pinId: props.initialPinnedDiscussionPinId ?? null,
    }), [props.initiallyPinnedInGroup, props.initialPinnedDiscussionPinId]);
    const [groupPin, setGroupPin] = useViewerActionRecord(
        "discussion-group-pin", groupScopedResourceId, initialGroupPin,
    );
    const isPinnedInGroup = groupPin.pinned;
    const pinnedDiscussionPinId = groupPin.pinId;
    const isDeleted = Boolean(props.isDeleted);
    const normalizedArgumentsCount = Number(props.initialArgumentsCount ?? 0);
    // SV-141 D1: a short post has no thoughts, so none of the composer doors below is offered on it.
    const isShortPost = Boolean(props.isShortPost);
    // Show the write-argument button whenever there is a count to display, and also at a zero count
    // on writable discussions so a fresh discussion gets a visible "write the first argument" entry.
    // Never on a short post (D1): it is both the thoughts count and a composer door. The mobile bar's
    // comment button reads this same predicate.
    const showArgumentCountButton = !isShortPost
        && Number.isFinite(normalizedArgumentsCount)
        && (normalizedArgumentsCount > 0 || !(isDeleted || isArchived));
    const showSeriesNavigation = Boolean(props.seriesNavigation?.backToSeriesHref);

    const localizer = props.localizer;
    const rowLayout = props.layout === "row";
    // D2: the host's menu slot, resolved once the host's refs attach (a layout effect, so before paint).
    // Keyed on the ref OBJECT, which a host keeps for its life: re-reading it on every commit re-renders
    // this component per commit even when the element is unchanged, and blew
    // `feedCardImageLoadCommitBudget` (26 commits for 12 image loads against a budget of 16).
    const menuSlotRef = props.menuSlot;
    const [menuSlotElement, setMenuSlotElement] = useState<HTMLElement | null>(null);
    useLayoutEffect(() => {
        setMenuSlotElement(menuSlotRef?.current ?? null);
    }, [menuSlotRef]);
    // The muted tone is appended to each control's own class hooks, so the tone can read the semantic
    // (agree / disagree / primary) off the same hook the active-state map uses.
    const toned = (extraClass: string): string =>
        rowLayout ? `${extraClass} ${getCtaToneClass("muted", extraClass)}` : extraClass;
    // Follow a post (question loop, Part A). Offered only on the FULL discussion page
    // (`showFloatingMobileBar` is that page's own marker; only `DiscussionContentDisplayer` sets it), to a
    // logged-in viewer who is not the author (`canEdit` is the author: see `isDiscussionAuthor` below),
    // on a live post. Everywhere else the hook is inert and asks nothing, which is what keeps a feed page
    // from issuing one follow read per card. Offered on every post, not only questions (open decision 3,
    // taken by default in the 2026-09-10 dispatch).
    const followEnabled = Boolean(props.showFloatingMobileBar) && isUserLoggedIn && !props.canEdit && !isDeleted;
    const discussionFollow = useDiscussionFollow(props.discussionPublicId, localizer, followEnabled);
    const isJsDomEnvironment = typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent);
    const accountAreaAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.accountArea, "discussion_account_area");
    const addToGroupAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.addToGroup, "discussion_add_to_group");
    const addToSeriesAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.addToSeries, "discussion_add_to_series");
    const reactAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.react, "discussion_react");
    const reportAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.report, "discussion_report");
    const saveAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.save, "discussion_save");
    const safetyAccountAccessDialog = withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.safety, "user_safety");

    // Block / mute the post's author from the same menu that already offers Report (design 2026-08-10,
    // slice A). Fetched on menu OPEN, not on mount: a feed page mounts this bar once per card, and the
    // safety state is only ever read by the two items inside the menu.
    const [safetyMenuOpen, setSafetyMenuOpen] = useState<boolean>(false);
    const safety = useUserSafetyActions({
        username: props.authorUsername,
        localizer,
        enabled: safetyMenuOpen,
    });
    // "Mute this post" (FRR 2b, FI-612). The full post page only (the follow gate's `showFloatingMobileBar`), a
    // logged-in viewer, a live post; the author included (the busy post is often their own). The state is read the
    // first time the `…` menu opens, for the same reason the safety state is: most members never open it.
    const muteEnabled = Boolean(props.showFloatingMobileBar) && isUserLoggedIn && !isDeleted;
    const discussionMute = useDiscussionNotificationMute(props.discussionPublicId, localizer, muteEnabled && safetyMenuOpen);

    // Newsletter growth work order D5/D8/A9 (slice 2b): "Send to my subscribers" for the post's owner, on a public
    // post only. Read on menu OPEN, like the safety items above; the item appears once the owner's ACTIVE
    // newsletters answer, and not at all for an owner with none (or a post the server does not call public).
    const sendPost = useSendPostToNewsletter(
        props.discussionPublicId,
        localizer,
        safetyMenuOpen && Boolean(props.canEdit) && isUserLoggedIn && !isDeleted && !isArchived && !props.isPrivateDiscussion,
    );

    const isWriteDisabled = isDeleted || isArchived;
    const writeDisabledReason = isDeleted
        ? localizer("discussion.deleted.write_disabled")
        : (isArchived ? localizer("discussion.archived.write_disabled") : null);
    const resolvedAgreementTitle = props.agreementAgreementTitle?.trim() || localizer("discussion.agreement");
    const resolvedDisagreementTitle = props.disagreementAgreementTitle?.trim() || localizer("discussion.disagreement");

    const openArgumentEditor = (surface: string) => {
        if (isWriteDisabled) {
            return;
        }
        try {
            // Instrumented at the OPEN, which is the step `thought_publish_started` cannot see: an
            // entry point that opens the composer and is abandoned otherwise looks identical to one
            // nobody pressed. `surface` separates the three doors, which is the whole point of
            // instrumenting it — the question this answers is which door people actually use.
            trackThoughtComposerOpened(surface);
            showThoughtTextEditorDialog({
                localizer,
                autoFocus: true,
                isUserLoggedIn: MetaHandler.isUserLoggedIn(),
                loggedInUsername: MetaHandler.getLoggedInUsername(),
                profileImageUrl: undefined,
                discussionPublicId: props.discussionPublicId,
                agreementAgreementTitle: resolvedAgreementTitle,
                disagreementAgreementTitle: resolvedDisagreementTitle,
                // Same composer as DiscussionThoughtsDisplayer's dialog, so it carries the same hook —
                // see the note there. Reached from the desktop count button and the mobile action bar.
                playwrightIdPrefix: "discussion-write-thought-dialog",
            });
        } catch (e) {
            logger.error("Failed to open argument editor from discussion buttons", e);
        }
    };

    // The bottom-right corner on the full discussion page. Contributing a group (rather than adding a
    // route rule to the FAB registry) is what makes this independent of the URL shape — see the
    // 2026-07-21 update in designs/04-live/2026-06-16-app-level-floating-action-buttons-design.md.
    //  - mobile           → an empty group: owns the corner, renders nothing (the bottom action bar is
    //                       the mobile affordance, and its comment button opens this same editor)
    //  - desktop, writable → "Share a thought", opening the argument editor for *this* post
    //  - otherwise        → bow out with null, so the registry's generic Create post FAB takes over
    // "Write an answer" on a question, "Share a thought" otherwise. One key for all three affordances
    // that open the same editor — the desktop FAB, the comment-count button's tooltip, and the mobile
    // action bar — so they cannot drift into saying different things about the same click.
    const writeThoughtLabelKey: LocalizationKey = props.isQuestion
        ? "discussion.question.answers.write"
        : "discussion.floating.write_thought";
    const canWriteThoughtFromCorner = isUserLoggedIn && !isWriteDisabled && !isShortPost;
    const contributedFloatingGroup: FloatingActionGroup | null = !props.showFloatingMobileBar
        ? null
        : isMobileViewport
            ? {scopeId: DISCUSSION_FLOATING_SCOPE, actions: []}
            : canWriteThoughtFromCorner
                ? {
                    scopeId: DISCUSSION_FLOATING_SCOPE,
                    container: "separate",
                    actions: [{
                        id: "write-thought",
                        look: {
                            icon: MessageSquareText,
                            labelKey: writeThoughtLabelKey,
                            variant: "primary",
                            size: "lg",
                        },
                        onClick: () => openArgumentEditor("floating_action"),
                        dataPlaywrightId: "floating-action-write-thought",
                    }],
                }
                : null;
    useFloatingActionGroup(DISCUSSION_FLOATING_SCOPE, contributedFloatingGroup, [
        props.showFloatingMobileBar,
        isMobileViewport,
        canWriteThoughtFromCorner,
        props.discussionPublicId,
        resolvedAgreementTitle,
        resolvedDisagreementTitle,
    ]);

    // End-of-post band (1f). The reply row follows the composer doors' own gate: hidden when writes are
    // disabled, shown to an anonymous reader (SV-47 gates at Publish, like the count button).
    // Never on a short post (SV-141 D1).
    const showEndOfPostReplyRow = !isWriteDisabled && !isShortPost;

    const getStatus = (err: any) => err?.response?.status ?? err?.status;
    const getServerMessage = (err: any) => err?.response?.data?.message ?? err?.message;

    // Re-seed the vote when this bar is pointed at a different discussion. The vote cannot use
    // `useViewerActionRecord` because its record is spread across four `useState`s, read individually
    // all through the render below — so it seeds through `resolveViewerActionRecord` and writes through
    // `rememberViewerActionRecord` instead, with this effect standing in for the hook's resync.
    //
    // This REPLACED a mount effect that wrote the props straight into the old viewer-agreement store,
    // which was the defect one layer down: on a Back-restored card the props are the PRE-vote DTO, so
    // the mount actively overwrote the correct remembered vote with "no vote". A control seeds FROM the
    // record; it never writes its own props back into it.
    useEffect(() => {
        applyVoteState(seedVoteState());
    }, [props.discussionPublicId]);

    // The bookmark / archived / pin prop-resync effects that used to live here are GONE, and their
    // absence is the fix, not an omission: each one blindly re-seeded from the prop, which is defect
    // path 2 in `ViewerActionRecords`. `useViewerActionRecord` now owns the resync and does it against
    // `record ?? prop`, so a stale bootstrap prop can no longer clobber the viewer's own action.

    useEffect(() => {
        if (!props.discussionPublicId) return;

        const unsubscribeAdded = onSeriesAddedToDiscussion((payload) => {
            if (!payload || payload.discussionPublicId !== props.discussionPublicId || !payload.series) {
                return;
            }
            if (isBookmarksSeries(payload.series as any)) {
                setIsSavedToBookmarks(true);
            }
        });

        const unsubscribeRemoved = onSeriesRemovedFromDiscussion((payload) => {
            if (!payload || payload.discussionPublicId !== props.discussionPublicId || !payload.series) {
                return;
            }
            if (isBookmarksSeries(payload.series as any)) {
                setIsSavedToBookmarks(false);
            }
        });

        return () => {
            unsubscribeAdded();
            unsubscribeRemoved();
        };
    }, [props.discussionPublicId]);

    function applyVoteState(state: DiscussionVoteState): void {
        setAgreementsCount(state.agreementsCount);
        setDisagreementsCount(state.disagreementsCount);
        setIsAgreeing(state.viewerAgreeing);
        setIsDisagreeing(state.viewerDisagreeing);
    }

    const toggleAgreement = async (mode: "AGREE" | "DISAGREE") => {
        if (isDeleted) {
            return;
        }
        // In-flight guard: the counts below are mutated after an awaited request, so a fast second
        // click (before the first settles) would double-apply the delta and desync the vote count.
        // Mirror the bookmark path's `savingToBookmarks` guard.
        if (isTogglingAgreement) {
            return;
        }
        setIsTogglingAgreement(true);

        const id = props.discussionPublicId;
        const username = MetaHandler.getLoggedInUsername();
        const currentState: DiscussionVoteState = {
            agreementsCount,
            disagreementsCount,
            viewerAgreeing: isAgreeing,
            viewerDisagreeing: isDisagreeing,
        };
        // The optimistic prediction, computed BEFORE the request from the same snapshot that picks the
        // withdraw-vs-add branch. Server truth replaces it when the endpoint returns a usable body; a
        // frontend deployed ahead of the backend falls back to this, never to nothing.
        const optimisticState = applyVoteToState(currentState, mode);
        const isWithdrawing = (mode === "AGREE" && isAgreeing) || (mode === "DISAGREE" && isDisagreeing);
        try {
            const response = isWithdrawing
                ? await apiClient.deleteAgreementInDiscussion(id, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                })
                : await apiClient.addAgreementInDiscussion(id, mode, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });
            if (!isWithdrawing) {
                trackDiscussionVote(mode === "AGREE" ? "upvote" : "downvote");
            }

            const nextState = parseDiscussionVoteState(response?.data) ?? optimisticState;
            const nextAgreement = agreementFromVoteState(nextState);
            applyVoteState(nextState);
            try {
                // Outlives this bar's unmount, so a Back-restored card seeds from it rather than from
                // the pre-vote DTO the restoration cache holds. Design §4d.
                rememberViewerActionRecord("discussion-vote", id, nextState);
            } catch {
            }
            // emit event: the viewer's agreement changed
            try {
                emitDiscussionViewerAgreementChanged({discussionPublicId: id, agreement: nextAgreement, username});
            } catch {
            }

            // The viewer's vote is a field of the cached discussion page-data response, and resting the
            // pointer on this card for 80 ms already prefetched (and cached) the PRE-vote copy. Drop it
            // here — on success, after the server state actually changed — or opening the discussion
            // within the 30 s TTL renders the vote as never cast. Prefetch design §2e.
            ClientFactory.invalidateDiscussionPageData(id, props.discussionSlug);
        } catch (e) {
            // EVERY branch logs before it returns — see DiscussionVoteTelemetry for why the previous
            // shape (three early returns above a single logger call) made the most diagnostic failures
            // invisible, including the session-loss one that looks exactly like a vanished vote.
            const handledAuthSessionError = isHandledAuthSessionError(e);
            const status = getStatus(e);
            logDiscussionVoteFailure({
                discussionPublicId: id,
                mode,
                operation: isWithdrawing ? "remove" : "add",
                reason: classifyDiscussionVoteFailure(status, handledAuthSessionError),
                status,
                error: e,
            });

            if (handledAuthSessionError) {
                return;
            }
            if (status === 403) {
                const message = getServerMessage(e) || localizer("typescript.discussion.user_cannot_interact");
                toast.error(message);
                return;
            }
            if (status && status >= 500) {
                toast.error(localizer("typescript.discussion.server_error_generic"));
                return;
            }
            toast.error(localizer("typescript.discussion.participation.update_failed"));
        } finally {
            setIsTogglingAgreement(false);
        }
    };

    const handleExclude = async () => {
        if (!props.initiallyViewerIsLeader) return;
        if (busy) return;
        if (!props.groupPublicId || !props.discussionPublicId) {
            toast.error(localizer("groups.modal.exclude.invalid_discussion_id"));
            return;
        }
        showExcludeDiscussionDialog(props.groupPublicId, props.discussionPublicId, localizer, () => {
            setIsExcluded(true);
            toast.success(localizer("group.exclude.success"));
        });
    };

    const handleUnexclude = async () => {
        if (!props.initiallyViewerIsLeader) return;
        if (busy) return;
        if (!props.groupPublicId || !props.discussionPublicId) {
            toast.error(localizer("groups.modal.exclude.invalid_discussion_id"));
            return;
        }

        const groupPublicId = props.groupPublicId;
        const discussionPublicId = props.discussionPublicId;

        showYesNoDialog({
            localizer,
            title: localizer("groups.unexclude_discussion"),
            message: <div>{localizer("groups.modal.unexclude.confirm_discussion")}</div>,
            yesLabel: localizer("groups.unexclude_discussion"),
            yesIcon: BookPlus,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            onYes: async () => {
                setBusy(true);
                const prev = isExcluded;
                setIsExcluded(false);
                try {
                    await groupApi.unexcludeDiscussion(groupPublicId, discussionPublicId);
                    toast.success(localizer("group.unexclude.success"));
                } catch (err) {
                    logger.error("Cancel exclusion (unexclude) failed", err);
                    setIsExcluded(prev);
                    // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                    throw err;
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    const handlePinInGroup = async () => {
        if (busy || !props.groupPublicId || !props.discussionPublicId || isDeleted) {
            return;
        }

        setBusy(true);
        try {
            const response = await ClientFactory.createPinnedDiscussion(props.groupPublicId, props.discussionPublicId);
            const pinnedDiscussion = response.data;
            setGroupPin({pinned: true, pinId: pinnedDiscussion?.pinId ?? pinnedDiscussion?.id ?? null});
            props.onPinnedInGroup?.(pinnedDiscussion);
            toast.success(localizer("group.pinned.pinned_success"));
        } catch (error: any) {
            logger.error("Pin discussion failed", error);
            toast.error(getSafeToastMessageFromError(error, localizer("group.pinned.add_failed")));
        } finally {
            setBusy(false);
        }
    };

    const handleUnpinFromGroup = async () => {
        if (busy || !props.groupPublicId || !props.discussionPublicId || isDeleted) {
            return;
        }

        const effectivePinId = pinnedDiscussionPinId ?? props.initialPinnedDiscussionPinId ?? null;
        if (effectivePinId == null) {
            toast.error(localizer("group.pinned.remove_failed"));
            return;
        }

        setBusy(true);
        try {
            await ClientFactory.deletePinnedDiscussion(props.groupPublicId, effectivePinId);
            setGroupPin({pinned: false, pinId: null});
            props.onUnpinnedFromGroup?.(props.discussionPublicId, effectivePinId);
            toast.success(localizer("group.pinned.unpinned_success"));
        } catch (error: any) {
            logger.error("Unpin discussion failed", error);
            toast.error(getSafeToastMessageFromError(error, localizer("group.pinned.remove_failed")));
        } finally {
            setBusy(false);
        }
    };

    /**
     * The bookmark write, expressed as a TARGET rather than as a flip.
     *
     * <p>It used to read `isSavedToBookmarks` and invert it, which is correct for the button and wrong
     * for anything that fires later: `EMPTY-STATES-81`'s undo lives inside a toast whose `onClick`
     * closes over the render that CREATED it, where `isSavedToBookmarks` is still `true` and
     * `savingToBookmarks` is still `true` — so re-running the flip would be blocked by the in-flight
     * guard and, past it, would have removed the post a second time. Caught by a red test before it
     * shipped, not by reading the code.
     *
     * <p>So: a target the caller states, an in-flight guard held in a REF rather than in the state a
     * stale closure would read, and exactly one write path — the toggle and the undo both come through
     * here, so the series events the dashboard listens to are emitted the same way in both directions.
     */
    const setBookmarksSaved = async (nextSaved: boolean): Promise<void> => {
        if (savingToBookmarksRef.current || !props.discussionPublicId) {
            return;
        }

        const currentlySaved = !nextSaved;
        savingToBookmarksRef.current = true;
        setSavingToBookmarks(true);
        setIsSavedToBookmarks(nextSaved);

        try {
            const { data: bookmarksSeries } = currentlySaved
                ? await bookmarksApi.removeDiscussionFromBookmarks(props.discussionPublicId)
                : await bookmarksApi.addDiscussionToBookmarks(props.discussionPublicId);
            const discussionPayload: DiscussionWithoutContentDTO = {
                publicId: props.discussionPublicId,
                slug: props.discussionSlug,
            };

            if (bookmarksSeries?.publicId) {
                if (currentlySaved) {
                    emitDiscussionRemovedFromSeries({
                        seriesPublicId: bookmarksSeries.publicId,
                        discussion: discussionPayload,
                        series: bookmarksSeries,
                    });
                    emitSeriesRemovedFromDiscussion({
                        discussionPublicId: props.discussionPublicId,
                        series: bookmarksSeries,
                    });
                } else {
                    emitDiscussionAddedToSeries({
                        seriesPublicId: bookmarksSeries.publicId,
                        discussion: discussionPayload,
                        series: bookmarksSeries,
                    });
                    emitSeriesAddedToDiscussion({
                        discussionPublicId: props.discussionPublicId,
                        series: bookmarksSeries,
                    });
                }
            }

            if (currentlySaved) {
                trackBookmarkRemoved("discussion");
            } else {
                trackBookmarkAdded("discussion");
            }

            // Same staleness as the vote path: `initiallySavedToBookmarks` and the series counts are
            // fields of the cached page-data response. Prefetch design §2e.
            ClientFactory.invalidateDiscussionPageData(props.discussionPublicId, props.discussionSlug);

            // `EMPTY-STATES-81` — on the member's own saved list, removal is destructive and the toast
            // is the last thing that can undo it.
            //
            // The undo re-enters `setBookmarksSaved` with an explicit target rather than writing a
            // second re-add path, so the add emits `onDiscussionAddedToSeries` exactly as an ordinary
            // save does — and that event is what puts the row back, through the listener the dashboard
            // already has. A local re-insert here would be a second writer of the saved list, and the
            // two would disagree the moment the server refused.
            //
            // It states the target instead of flipping: this closure captures the render that CREATED
            // the toast, where the post still reads as saved and the write still reads as in flight.
            //
            // Only on removal, and only on that surface. There is nothing to undo about saving, and on
            // the feed or the discussion page removal destroys nothing the member is looking at.
            //
            // Two calls rather than one with an `undefined` second argument: `DiscussionButtons.test`
            // asserts the plain save toast by exact arguments, and a trailing `undefined` fails that
            // assertion. It is a named, deliberate test and it is right — the unchanged path should be
            // byte-identical, which is the whole claim this prop makes (rule 10).
            const successMessage = localizer(currentlySaved
                ? "typescript.discussion.bookmarks.removed"
                : "typescript.discussion.bookmarks.saved");
            if (props.viewerSavedList && currentlySaved) {
                toast.success(successMessage, {
                    duration: 10000,
                    action: {
                        // The stable hook rides INSIDE the label, exactly as the group-ban suggestion
                        // toast does: sonner renders the label in its own action button, so a spec can
                        // find and click it without depending on the localized word (rule 20).
                        label: <span data-playwright-id="discussion-unsave-undo-action">{localizer("literal.undo")}</span>,
                        // An explicit target, never a flip — see `setBookmarksSaved`.
                        onClick: () => { void setBookmarksSaved(true); },
                    },
                });
            } else {
                toast.success(successMessage);
            }
        } catch (error) {
            setIsSavedToBookmarks(currentlySaved);
            logger.error("Bookmarks save update failed", error);
            toast.error(localizer("typescript.discussion.bookmarks.update_failed"));
        } finally {
            savingToBookmarksRef.current = false;
            setSavingToBookmarks(false);
        }
    };

    /** What the button does: flip whatever the control is currently showing. */
    const toggleBookmarksSave = (): Promise<void> => setBookmarksSaved(!isSavedToBookmarks);

    const discussionPathKey = props.discussionSlug || props.discussionPublicId;
    const sectionBase = `/discussion/${discussionPathKey}`;
    const previousInSeriesLabel = props.seriesNavigation?.previous?.href
        ? localizer("discussion.series.navigation.previous")
        : undefined;
    const nextInSeriesLabel = props.seriesNavigation?.next?.href
        ? localizer("discussion.series.navigation.next")
        : undefined;
    const backToSeriesLabel = props.seriesNavigation?.backToSeriesHref
        ? localizer("discussion.series.navigation.back")
        : undefined;
    const previousInSeriesTitle = !props.seriesNavigation?.previous?.href
        ? undefined
        : (props.seriesNavigation.previous.title?.trim()
            ? localizer("discussion.series.navigation.previous_with_title", props.seriesNavigation.previous.title.trim())
            : previousInSeriesLabel);
    const nextInSeriesTitle = !props.seriesNavigation?.next?.href
        ? undefined
        : (props.seriesNavigation.next.title?.trim()
            ? localizer("discussion.series.navigation.next_with_title", props.seriesNavigation.next.title.trim())
            : nextInSeriesLabel);
    const backToSeriesTitle = !props.seriesNavigation?.backToSeriesHref
        ? undefined
        : (props.seriesNavigation.backToSeriesTitle?.trim() || backToSeriesLabel);

    const handleArchiveToggle = () => {
        if (archiveSubmitting || deleteSubmitting) {
            return;
        }

        const currentlyArchived = isArchived;

        showYesNoDialog({
            localizer,
            title: localizer(currentlyArchived ? "discussion.manage.unarchive.confirm.title" : "discussion.manage.archive.confirm.title"),
            message: <div>{localizer(currentlyArchived ? "discussion.manage.unarchive.confirm.message" : "discussion.manage.archive.confirm.message")}</div>,
            yesLabel: localizer(currentlyArchived ? "discussion.manage.unarchive" : "discussion.manage.archive"),
            yesIcon: currentlyArchived ? RotateCcw : Archive,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            onYes: async () => {
                setArchiveSubmitting(true);
                try {
                    const response = currentlyArchived
                        ? await apiClient.unarchiveDiscussion(props.discussionPublicId)
                        : await apiClient.archiveDiscussion(props.discussionPublicId);
                    const nextArchived = Boolean(response.data?.archived);
                    setIsArchived(nextArchived);
                    toast.success(localizer(nextArchived ? "discussion.manage.archive.success" : "discussion.manage.unarchive.success"));
                } catch {
                    // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                    throw new Error("Failed to update archive state");
                } finally {
                    setArchiveSubmitting(false);
                }
            },
        });
    };

    // Mark-answered / reopen. One request, no confirmation dialog: both are cheap, reversible and
    // non-destructive, unlike archive and delete above. The returned QuestionStateView is the ONLY thing
    // that updates the badges — we do not guess the next state locally, because the server derives
    // `ANSWERED` from the accepted answer's visibility and can legitimately return something other than
    // what the verb's name suggests.
    // REVIEW facet dialog. Owned here rather than by the page because the menu entry that opens it is
    // here, and the dialog's own state is entirely self-contained — the only thing that leaves it is
    // the saved result, which goes back up through onReviewFacetChanged.
    const [reviewFacetDialogOpen, setReviewFacetDialogOpen] = useState<boolean>(false);

    const [questionStateSubmitting, setQuestionStateSubmitting] = useState<boolean>(false);
    const handleQuestionStateVerb = async (verb: "resolve" | "reopen") => {
        if (questionStateSubmitting) {
            return;
        }
        setQuestionStateSubmitting(true);
        try {
            const response = verb === "resolve"
                ? await ClientFactory.resolveQuestion(props.discussionPublicId)
                : await ClientFactory.reopenQuestion(props.discussionPublicId);
            props.onQuestionStateChanged?.(response.data);
            toast.success(localizer(verb === "resolve"
                ? "discussion.question.toast.answered"
                : "discussion.question.toast.reopened"));
        } catch (e) {
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.error(e);
            // Surface the SERVER's message when it has one — `reopen` is legitimately refused while an
            // answer is accepted, and "unaccept the current answer first" is the only useful thing to say.
            toast.error(getSafeToastMessageFromError(e, localizer("discussion.question.error.generic")));
        } finally {
            setQuestionStateSubmitting(false);
        }
    };

    const handleDeleteDiscussion = () => {
        if (archiveSubmitting || deleteSubmitting) {
            return;
        }

        showYesNoDialog({
            localizer,
            title: localizer("discussion.manage.delete.confirm.title"),
            message: (
                <div className="flex flex-col gap-2">
                    {/* This path navigates away on success, so an Undo toast would be destroyed by its
                        own navigation. The recovery route is told to the member up front instead.

                        There used to be a second paragraph ABOVE this one, saying the delete "cannot be
                        undone in normal author UI". It was written before the recovery list existed and
                        never revised, so the dialog contradicted itself in one box, technically (rule 12:
                        "deleted-state shell") and in the direction that costs the most: a member who
                        believes the first line has no reason to read the second as anything but a
                        qualification. On 2026-08-14 one did exactly that and wrote to support instead of
                        restoring the post himself. Do not reintroduce a permanence claim here — the only
                        true sentence about this action is the one below. */}
                    <p>{localizer("discussion.manage.delete.confirm.recoverable")}</p>
                </div>
            ),
            yesLabel: localizer("discussion.manage.delete"),
            yesIcon: Trash2,
            noLabel: localizer("literal.cancel"),
            noIcon: X,
            destructive: true,
            onYes: async () => {
                setDeleteSubmitting(true);
                try {
                    await apiClient.deleteDiscussion(props.discussionPublicId);
                    if (!isJsDomEnvironment) {
                        navigateToUrl(sectionBase, { source: "discussion-delete" });
                    }
                } catch {
                    // YesNoDialog surfaces the failure banner on re-throw; don't also toast.
                    throw new Error("Failed to delete discussion");
                } finally {
                    setDeleteSubmitting(false);
                }
            },
        });
    };

    const actionItems: DropdownItem[] = [];

    if (!isDeleted) {
        actionItems.push(
            {
                id: "add-to-group",
                icon: Share2,
                text: localizer("typescript.discussion.add_to_group"),
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    // Code-split with the sibling call in MakePrivateDiscussionReachableDialog.tsx.
                    // Both had to move together: cutting only one left the 48 KB picker on the eager
                    // graph through the other, measuring as no change at all. Rule 26 helper, and the
                    // `{ default: m }` wrap is the named-export idiom (see bodyClipPlayers.ts).
                    void importChunkWithRetryAndReload(
                        () =>
                            import("@/main/react/Dialog/AddDiscussionToGroupDialog").then((m) => ({
                                default: m,
                            })),
                        "add-discussion-to-groups-dialog",
                    ).then((module) =>
                        module.default.showAddDiscussionToGroupsDialog(
                            props.discussionPublicId,
                            props.localizer,
                            {
                                isPrivateDiscussion: Boolean(props.isPrivateDiscussion),
                                isDiscussionAuthor: Boolean(props.canEdit),
                            },
                        ),
                    );
                },
                requiresLogin: true
                , accountAccessDialog: addToGroupAccountAccessDialog
            },
        );

        // `EMPTY-STATES-100` (wave-14 Q2) — Add to series is PROMOTED into the footer on the viewer's
        // own saved list, so it is withheld from the menu there. One action, one presentation, never
        // both: the promoted pill and this item are the same verb, and offering it twice in one footer
        // is the defect `DISCUSSION-30` already spells out for Share.
        //
        // ⚠️ Register correction: the row states the overflow menu "has **no** add-to-series entry".
        // That has been false since 2026-03-26 (`59ca77fd72`). What was actually missing on that route
        // was the entry being FINDABLE — buried under `⋯` on the one list whose subject is a
        // collection the member is trying to organise.
        if (!props.viewerSavedList) {
            actionItems.push({
                id: "add-to-series",
                icon: ListPlus,
                text: localizer("typescript.discussion.add_to_series"),
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    showAddDiscussionToSeriesDialog(props.discussionPublicId, props.localizer);
                },
                requiresLogin: true,
                accountAccessDialog: addToSeriesAccountAccessDialog
            });
        }

        if (props.groupPublicId && props.initiallyViewerIsLeader) {
            actionItems.push({
                id: isPinnedInGroup ? "unpin-from-group" : "pin-to-group",
                icon: Pin,
                text: localizer(isPinnedInGroup ? "group.pinned.action.unpin" : "group.pinned.action.pin"),
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    void (isPinnedInGroup ? handleUnpinFromGroup() : handlePinInGroup());
                },
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
            });
        }
    }

    const discussionShareUrl = () => {
        const discussionPath = props.discussionSlug ? `/discussion/${props.discussionSlug}` : `/discussion/${props.discussionPublicId}`;
        return (typeof window !== 'undefined' ? window.location.origin : '') + discussionPath;
    };

    // Share is a first-class button in the row (and the mobile bar) from `sm` up, and folds into the `⋯`
    // menu below it (`DISCUSSION-30`, 2026-08-24) — one action, two presentations, never both at once.
    // Copy-link keeps its own overflow slot at every width for the "silent copy" variant.
    const handleShare = async () => {
        // `DISCUSSION-58`. The re-entrancy check is FIRST, above `trackShare`: the row's cost is not
        // only two sheets, it is a double-counted `trackShare` + `SHARE_INITIATED` pair on one member
        // intent, which makes the channel funnel un-joinable. The ref, not the state, because the
        // second tap is delivered to the handler the previous render closed over.
        if (sharePendingRef.current) {
            return;
        }
        sharePendingRef.current = true;
        setSharePending(true);
        try {
            trackShare("discussion", "discussion_actions");
            await shareUrl(discussionShareUrl(), props.localizer("typescript.discussion.share"), localizer,
                {
                    entityType: "DISCUSSION",
                    entityId: props.discussionPublicId,
                    entityTitle: props.discussionTitle,
                    authorDisplayName: props.authorDisplayName,
                    // D6 / A6: a question's share asks for the reader's take rather than announcing the
                    // post. One field on the context this button already builds — the SENTENCE is
                    // composed in the chokepoint, so no second share control appears here (rule 23) and
                    // the next share affordance inherits the behaviour without being threaded.
                    isQuestion: props.isQuestion,
                },
                // Quote on your page (design §10.2). Signed-in ONLY, and decided here rather than in the
                // drawer, which has no viewer identity at all: an anonymous viewer gets `undefined` and
                // the row is ABSENT rather than disabled, matching the existing anonymous action gates.
                //
                // The id is stashed rather than put in the URL because the composer entry route
                // (`/create/discussion`) 302s to `/create/discussion/{draftPublicId}`, which drops a query
                // string. sessionStorage survives that redirect, is scoped to this tab, and is read once
                // and cleared by the composer.
                //
                // `/new`, never the resuming `/create/discussion`: that route reopens the member's
                // unfinished draft (FI-256), so the quote card landed inside whatever they were last
                // writing (gate `009b009b44`, 2026-09-21). A quote starts a new post.
                MetaHandler.getLoggedInUsername()
                    ? () => {
                        stashPendingQuote(props.discussionPublicId);
                        navigateToUrl(CREATE_NEW_DISCUSSION_PATH, { source: "share-drawer-quote" });
                    }
                    : undefined);
        } finally {
            // `shareUrl` resolves when the OS sheet closes, when the drawer has opened, or when the
            // clipboard write has landed — i.e. exactly when there is something on screen for the
            // member to act on. `finally` rather than the happy path: a rejected share must not leave
            // the control disabled for the rest of the page's life.
            sharePendingRef.current = false;
            setSharePending(false);
        }
    };

    // Both outward-facing verbs are withheld on a deleted post (EMPTY-STATES-24, 2026-08-14). They hand
    // out the public `/discussion/<slug>` URL of content the member has taken down, and the share path
    // additionally fires `trackShare` — an engagement event attributed to a post that no longer exists.
    // Until this guard, share was not merely still present: with voting disabled, the bookmark removed
    // and the menu pruned, it was the ONLY fully-enabled control on a deleted row, which makes it the
    // one a member is most likely to hit while probing what the row can do.
    if (!isDeleted) {
        // `DISCUSSION-30`'s other half: the phone's Share. `sm:hidden` is what makes this the SAME
        // control as the button above rather than a second offer of the same action — the two never
        // render together at any width.
        //
        // Pushed INSIDE this `!isDeleted` guard on purpose. The menu withholds itself when it would open
        // onto nothing (`EMPTY-STATES-24`), and a share item pushed unconditionally would resurrect an
        // empty `…` on a deleted post — a menu whose single entry is hidden by CSS from `sm` up. Inside
        // the guard the menu already carries add-to-group, add-to-series and copy-link, so it is never
        // share-only and never empty.
        //
        // Round 42 D3 (`DASHBOARD-66`): on the viewer's own Saved list, below `sm`, the vote pair moves into
        // this menu so the footer fits one line with the bookmark (the list's own verb) on it. `sm:hidden`
        // here and `hidden sm:flex` on the pair make them ONE control at every width, the Share / Follow
        // split above. The label is the verb the item performs, so it flips once the vote is cast
        // ("Upvote" -> "Remove upvote"), the Follow / Stop following convention. The counts leave the card
        // on the phone Saved list only; every other surface keeps the pair at every width.
        if (props.viewerSavedList && showViewerReactions) {
            actionItems.push(
                {
                    id: "saved-list-upvote",
                    icon: ArrowBigUp,
                    text: localizer(isAgreeing ? "discussion.menu.remove_upvote" : "discussion.agreement"),
                    extraClass: "sm:hidden",
                    disabled: isTogglingAgreement,
                    onClick: (ev?: Event) => {
                        try {
                            ev?.preventDefault();
                            ev?.stopPropagation();
                        } catch {
                        }
                        void toggleAgreement("AGREE");
                    },
                    requiresLogin: true,
                    accountAccessDialog: reactAccountAccessDialog,
                },
                {
                    id: "saved-list-downvote",
                    icon: ArrowBigDown,
                    text: localizer(isDisagreeing ? "discussion.menu.remove_downvote" : "discussion.disagreement"),
                    extraClass: "sm:hidden",
                    disabled: isTogglingAgreement,
                    onClick: (ev?: Event) => {
                        try {
                            ev?.preventDefault();
                            ev?.stopPropagation();
                        } catch {
                        }
                        void toggleAgreement("DISAGREE");
                    },
                    requiresLogin: true,
                    accountAccessDialog: reactAccountAccessDialog,
                },
            );
        }

        // Existing key, no new copy: the same `typescript.discussion.share` the button uses.
        actionItems.push({
            id: "share",
            icon: Share,
            text: localizer("typescript.discussion.share"),
            extraClass: "sm:hidden",
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void handleShare();
            },
            requiresLogin: false,
        });

        // Follow a post (question loop, Part A): the phone's half of the Follow pill, the way Share above
        // is the phone's half of its button. `sm:hidden` here and `hidden sm:flex` on the pill make them ONE
        // control at every width, reading one state (`useDiscussionFollow`). The label is the verb the item
        // performs, "Follow" or "Stop following", because a menu item reads as an action, never a state.
        if (followEnabled) {
            actionItems.push({
                id: "follow",
                icon: Bell,
                text: localizer(discussionFollow.following
                    ? "typescript.discussion.stop_following"
                    : "typescript.discussion.follow"),
                extraClass: "sm:hidden",
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    void discussionFollow.toggle();
                },
                requiresLogin: false,
            });
        }

        // "Mute this post" / "Unmute this post" (FRR 2b): the verb the item performs, like Follow above.
        if (muteEnabled) {
            actionItems.push({
                id: "notification-mute",
                icon: discussionMute.muted ? Bell : BellOff,
                text: localizer(discussionMute.muted
                    ? "notifications.unmute_post.action"
                    : "notifications.mute_post.action"),
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    void discussionMute.toggle("post_menu");
                },
                requiresLogin: false,
            });
        }

        actionItems.push({
            id: "copy-link",
            icon: Link,
            text: localizer("typescript.discussion.copy_link"),
            onClick: async () => {
                await copyUrlToClipboard(discussionShareUrl(), localizer,
                    { entityType: "DISCUSSION", entityId: props.discussionPublicId });
            },
            requiresLogin: false
        });
    }

    if (props.canEdit) {
        // QUESTION facet: the asker's own status control. `canEdit` is the post's authorship, which is
        // exactly the asker — the same person the four /ajax/question verbs authorize, so a viewer who
        // sees this item is a viewer the server will accept. OPEN → "Mark as answered";
        // RESOLVED → "Reopen"; ANSWERED → nothing (see the props doc).
        if (props.isQuestion && !isDeleted && props.questionState !== "ANSWERED") {
            const reopening = props.questionState === "RESOLVED";
            actionItems.push({
                id: reopening ? "reopen-question" : "mark-question-answered",
                icon: reopening ? RotateCcw : Check,
                text: localizer(reopening ? "discussion.question.reopen" : "discussion.question.mark_answered"),
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    void handleQuestionStateVerb(reopening ? "reopen" : "resolve");
                },
            });
        }

        // QUESTION facet, SV-130 "Ask someone to answer": author-only, on an OPEN question only. The
        // server enforces the same three things (author, QUESTION, not ANSWERED/RESOLVED) and answers a
        // bare 403 otherwise, so a viewer who sees this item is a viewer it will accept. Withheld on a
        // resolved or answered question rather than shown-and-refused, for the reason the review item
        // below gives. D11: an ask already sent is left alone when the question resolves.
        // Round-29 D12/A19: also withheld unless the server's viewer-specific `askToAnswerAvailable` bit is
        // true (false for a held-out asker, and absent reads as false), so the holdout never shows an item
        // that refuses.
        if (props.isQuestion && !isDeleted && props.questionState === "OPEN" && props.askToAnswerAvailable === true) {
            actionItems.push({
                id: "ask-to-answer",
                icon: UserPlus2,
                text: localizer("discussion.ask_to_answer"),
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    showAskToAnswerDialog(props.discussionPublicId, localizer);
                },
            });
        }

        // REVIEW facet (§5.7). Author-only, and `canEdit` IS the post's authorship — the same person
        // /ajax/review authorizes, so a viewer who sees this item is a viewer the server will accept.
        //
        // Deliberately NOT offered on a post that already carries QUESTION. The server refuses that
        // combination rather than swapping (decision 22), and offering a control whose only outcome is
        // a refusal is worse than offering none — the same reasoning that withholds a verb in the
        // question's ANSWERED state above.
        if (!isDeleted && !props.isQuestion) {
            const hasReview = Boolean(props.reviewSubjectPublicId && props.reviewRatingHalfStars);
            actionItems.push({
                id: hasReview ? "edit-review-details" : "add-review-details",
                icon: Star,
                text: localizer(hasReview
                    ? "discussion.facet.review.edit.menu_edit"
                    : "discussion.facet.review.edit.menu_add"),
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
                onClick: (ev?: Event) => {
                    try {
                        ev?.preventDefault();
                        ev?.stopPropagation();
                    } catch {
                    }
                    setReviewFacetDialogOpen(true);
                },
            });
        }

        // `EMPTY-STATES-124` (V67 a): inside the `!isDeleted` guard like every other author item. On a
        // deleted post it was the menu's ONLY item, so the `…` opened onto one entry unrelated to the
        // recovery the deleted tab exists for. Without it the menu is empty and withholds itself
        // (`EMPTY-STATES-24`); Restore stays the pill below the card.
        if (!isDeleted) {
            actionItems.push({
                id: "metrics",
                icon: ChartArea,
                text: localizer("discussion.section.metrics"),
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
                href: `${sectionBase}/metrics`,
            });
        }

        if (!isDeleted) {
            // "Create Translation" was removed on 2026-08-20. It linked to
            // `/create/discussion/from-discussion/{id}?newTranslation=true`, and NOTHING read
            // `newTranslation` — CreateController takes only an optional `languageTag`, which the link
            // did not pass either. So it resolved to the identical route and identical behaviour as the
            // Edit action below: the draft is stamped with the source's `discussionPublicId`, and
            // `publishDraft` then takes the UPDATE branch that overwrites the live row in place. An
            // author who wrote a translation and published had REPLACED their original post — no
            // sibling row, no translation group, `linkAsTranslation` never called. That is exactly what
            // DiscussionTranslationGroupService's class javadoc warns against. Rebuilding it means a
            // fresh draft (`discussionPublicId == null`) plus an explicit link step after publish; see
            // designs/08-audits-and-process/2026-08-20-marketing-copy-claims-vs-product-audit.md §3.

            actionItems.push({
                id: "edit",
                icon: Edit3,
                text: localizer("typescript.discussion.edit"),
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
                href: `/create/discussion/from-discussion/${props.discussionPublicId}`,
            });

            // D5: right after Edit. D8: once per post per newsletter, so once every active newsletter has an issue
            // from this post the item stays as the disabled state "Sent to subscribers". Disabled while a request
            // is out too: a second tap would race the server's unique index (amendment at the wave-1 landing).
            if (!props.isPrivateDiscussion && sendPost.choices && sendPost.choices.length > 0) {
                actionItems.push({
                    id: "send-to-newsletter",
                    icon: Mail,
                    text: localizer(sendPost.allSent ? "newsletter.send_post.sent" : "newsletter.send_post.menu"),
                    disabled: sendPost.allSent || sendPost.pending,
                    requiresLogin: true,
                    accountAccessDialog: accountAreaAccountAccessDialog,
                    onClick: () => void sendPost.start(),
                });
            }
        }

        // The REVERSAL of a state the post is already in is offered wherever the author meets the post,
        // not only on the full discussion page (EMPTY-STATES-5, 2026-08-14). `showManageActions` is
        // passed by `DiscussionContentDisplayer` alone, so the dashboard's archived sub-tab — the only
        // screen in the product that lists a member's archived posts, and therefore the only place
        // someone goes to bring one back — showed every card's "…" menu without the one verb the route
        // exists for. Archiving something takes a full page load per item to undo.
        //
        // Deliberately asymmetric: `isArchived` opens the door for unarchive only. Delete stays behind
        // `showManageActions`, and so does archiving a live post — neither is a way OUT of a state, and
        // putting either on every own-post card in every feed is a different product decision. The
        // enclosing block is already `props.canEdit`, so this can only ever reach the author.
        if ((props.showManageActions || isArchived) && !isDeleted) {
            actionItems.push({
                id: "archive-discussion",
                icon: isArchived ? RotateCcw : Archive,
                text: localizer(isArchived ? "discussion.manage.unarchive" : "discussion.manage.archive"),
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
                onClick: () => void handleArchiveToggle(),
            });
        }

        if (props.showManageActions && !isDeleted) {
            actionItems.push({
                id: "delete-discussion",
                icon: Trash2,
                text: localizer("discussion.manage.delete"),
                requiresLogin: true,
                accountAccessDialog: accountAreaAccountAccessDialog,
                onClick: () => void handleDeleteDiscussion(),
                destructive: true,
            });
        }
    }

    const moderationItems: DropdownItem[] = [];
    const showGroupModeration = Boolean((props.groupPublicId) && props.initiallyViewerIsLeader);
    if (showGroupModeration && !isDeleted) {
        moderationItems.push({
            id: "exclude-toggle",
            icon: isExcluded ? BookPlus : BookMinus,
            text: isExcluded ? (localizer("groups.unexclude_discussion")) : (localizer("groups.exclude_discussion")),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                void (isExcluded ? handleUnexclude() : handleExclude());
            },
            requiresLogin: true,
            accountAccessDialog: accountAreaAccountAccessDialog,
        });

    }

    if (!isDeleted) {
        moderationItems.push({
            id: "report-discussion",
            icon: Flag,
            text: localizer("report.modal.submit_button.entity", localizer("report.entity.discussion")),
            onClick: (ev?: Event) => {
                try {
                    ev?.preventDefault();
                    ev?.stopPropagation();
                } catch {
                }
                showReportEntityDialog(localizer, "discussion", props.discussionPublicId);
            },
            requiresLogin: true,
            accountAccessDialog: reportAccountAccessDialog,
            destructive: true,
        });

        // Directly under Report. `canOfferSafetyControls` suppresses the pair on your own post and on the
        // support account (D29) — hidden rather than inert.
        moderationItems.push(...buildUserSafetyDropdownItems(safety, localizer, safetyAccountAccessDialog));
    }

    const dropdownSections: DropdownSection[] = [];
    if (actionItems.length > 0) dropdownSections.push({id: "actions", title: null, items: actionItems});
    if (moderationItems.length > 0) dropdownSections.push({id: "moderation", title: null, items: moderationItems});

    // The `…` menu, built once and placed by the caller (`menuSlot`, D2) or at the end of the row.
    // Never filed as its own row, found by the tree sweep the icon-button-names batch added: the same
    // unnamed `…` trigger as `GROUP-6`, on the busiest page in the product. Existing key — a new label
    // is a 24-locale bill (AGENTS.md rule 11). In `layout="row"` it rests muted like its row.
    const moreMenu = dropdownSections.length > 0 ? (
        <CtaButtonDropdown
            icon={MoreHorizontal}
            ariaLabel={localizer("typescript.discussion.preview.publish_settings.more_options")}
            playwrightId="discussion-more-options-button"
            size={"sm"}
            variant="ghost"
            extraClass={toned("discussion-more-options-button")}
            dropdownSections={dropdownSections}
            onOpenChange={setSafetyMenuOpen}
        />
    ) : null;

    return (
        <>
        {/* Gap widens with available WIDTH, not with the pointer type. The row is ~300px of 44px tap
            targets (getCtaSizeTouchTargetClass) against ~334px of card-footer content on a ~390px
            phone, so the old touch-only `gap-3` bump was the term that pushed it over and wrapped the
            `…` overflow button onto its own line — while buying nothing visually, since at min-w-11
            each pill's painted box already IS 44px wide and 6px between them reads fine. Tap targets
            themselves stay pointer-keyed and unchanged; the joined vote pair stays one control
            (ButtonGroup is gapless). See
            designs/01-now/2026-07-29-mobile-avatar-load-resilience-and-card-action-row-fit-design.md. */}
        <div className={rowLayout
            ? "flex w-full max-w-full flex-wrap items-center justify-between gap-1.5 sm:gap-2 md:gap-3"
            : "ml-auto flex max-w-full flex-wrap items-center justify-end gap-1.5 sm:gap-2 md:gap-3"}>
            {showArgumentCountButton ? (
                <CtaButton
                    icon={MessageSquareText}
                    buttonCount={normalizedArgumentsCount}
                    size="sm"
                    variant="ghost"
                    // SV-47: gate at Publish, not before writing.
                    requiresLogin={false}
                    disabled={isWriteDisabled}
                    disabledReason={writeDisabledReason}
                    displayTextOnMobile={false}
                    extraClass={toned("discussion-write-argument-button")}
                    playwrightId="discussion-thoughts-count"
                    title={localizer(writeThoughtLabelKey)}
                    /*
                     * `DISCUSSION-41` — two counts for the same conversation, 90 CSS px apart, the
                     * larger one unlabelled. The article footer stated a bare "110" while the section
                     * strip immediately below it read "Conversations 43"; both are correct (110
                     * thoughts across 43 conversations) and nothing on the screen said they counted
                     * different things, so the natural reading was that one of them was wrong.
                     *
                     * Two edits, and they only work together.
                     *
                     * 1. `ariaLabel`. Without it this button's accessible name came from its CONTENT,
                     *    which is the digits — a screen reader announced "110, button" and never the
                     *    action. That is this row's complaint restated at the assistive-tech layer, and
                     *    it is the same label/destination agreement the 2026-08-24 change established
                     *    for the phone bar: the name must be the ACT, because the click is the act.
                     *
                     * 2. `text`. With the name pinned explicitly above, the visible word can annotate
                     *    the NUMBER without becoming the control's name — which is what makes it safe
                     *    here and would not have been safe on its own. `discussion.thoughts`
                     *    ("thoughts", lowercase) is an existing key in all 25 catalogs; no new copy
                     *    (rule 11). `displayTextOnMobile={false}` was already set, so the word appears
                     *    only from `sm` up and the ~390px phone action row keeps its measured width.
                     *
                     * Scoped to `showFloatingMobileBar`, which only `DiscussionContentDisplayer` sets:
                     * that is the discussion PAGE's article footer, the one surface where the strip's
                     * labelled "Conversations 43" sits 90px below. `DiscussionCardDisplayer` renders
                     * the same component in the feed, where there is no second count to reconcile and
                     * a unit word on every card is a different, wider design change.
                     *
                     * NOT changed, deliberately: the destination. The phone bar's count button was
                     * re-pointed at the composer on 2026-09-01 at the operator's explicit request, and
                     * its label, enabled state and destination stay read off ONE predicate.
                     */
                    ariaLabel={localizer(writeThoughtLabelKey)}
                    text={props.showFloatingMobileBar ? localizer("discussion.thoughts") : undefined}
                    onClick={(ev) => {
                        try {
                            ev?.preventDefault();
                            ev?.stopPropagation();
                        } catch {
                        }
                        openArgumentEditor("discussion_actions");
                    }}
                />
            ) : null}

            {/* On a deleted post the vote pair is a STATIC COUNT, not a disabled control
                (EMPTY-STATES-17, 2026-08-14). The disabled treatment was measured at 1.11:1 in light
                and 1.05:1 in dark against the card fill — not a state change a person can see — and the
                little that did register was a FILLED PILL, which is the vocabulary this design language
                uses for a SELECTED control, so it read as "both buttons pressed". The active arrow also
                kept its full-strength brand green, byte-identical to a live post's. Removing the control
                shape removes the whole ambiguity rather than trying to win a contrast argument inside
                it; the explanation that used to live only in a hover `disabledReason` — which a touch
                device never shows — is now the card's own "Deleted post" badge. */}
            {/* The same static presentation now serves a SECOND case: the viewer's own work
                (`DASHBOARD-25`). The reasoning transfers exactly — a control whose only outcome is
                "you cannot do that to yourself" is worse than no control — and the counts are the part
                an author actually wants. Distinct playwright id per case, because "deleted" and "mine"
                are different states and a spec must be able to tell them apart. */}
            {isDeleted || !showViewerReactions ? (
                <span
                    className="flex items-center gap-3 text-sm text-muted-foreground"
                    data-playwright-id={isDeleted ? "discussion-deleted-static-votes" : "discussion-own-work-static-votes"}
                >
                    <span className="inline-flex items-center gap-1">
                        <ArrowBigUp className="size-4" aria-hidden={true}/>
                        <span aria-label={`${localizer("discussion.agreement")}: ${agreementsCount}`}>{agreementsCount}</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <ArrowBigDown className="size-4" aria-hidden={true}/>
                        <span aria-label={`${localizer("discussion.disagreement")}: ${disagreementsCount}`}>{disagreementsCount}</span>
                    </span>
                </span>
            ) : (
            // Round 42 D3: on the viewer's own Saved list the pair is a `⋯` item pair below `sm` (see
            // `saved-list-upvote`). `hidden sm:flex`, not `sm:inline-flex`, the Share button's reason.
            <div className={props.viewerSavedList ? "hidden items-center sm:flex" : "flex items-center"}>
                <VoteContainer
                    leftButtonIcon={ArrowBigUp}
                    leftButtonTitle={localizer("discussion.agreement")}
                    leftButtonCount={agreementsCount}
                    size={"sm"}
                    localizer={localizer}
                    leftButtonActive={isAgreeing}
                    displayTextOnMobile={false}
                    disabled={isDeleted || isTogglingAgreement}
                    disabledReason={isDeleted ? localizer("discussion.deleted.write_disabled") : null}
                    leftButtonExtraClass={toned("agreement-discussion-button")}
                    leftButtonActiveClass={undefined}
                     leftInteractionEffect="agree"
                     leftCountFeedback="pulse"
                    rightButtonIcon={ArrowBigDown}
                    rightButtonTitle={localizer("discussion.disagreement")}
                    rightButtonCount={disagreementsCount}
                    rightButtonActive={isDisagreeing}
                    rightButtonExtraClass={toned("disagreement-discussion-button")}
                     rightInteractionEffect="disagree"
                     rightCountFeedback="pulse"
                    requiresLogin={true}
                    accountAccessDialog={reactAccountAccessDialog}
                    onLeftClick={async (ev?: Event | React.MouseEvent<HTMLButtonElement>) => {
                        try {
                            (ev as React.MouseEvent)?.preventDefault();
                            (ev as React.MouseEvent)?.stopPropagation();
                        } catch {
                        }
                        await toggleAgreement("AGREE");
                    }}
                    onRightClick={async (ev?: Event | React.MouseEvent<HTMLButtonElement>) => {
                        try {
                            (ev as React.MouseEvent)?.preventDefault();
                            (ev as React.MouseEvent)?.stopPropagation();
                        } catch {
                        }
                        await toggleAgreement("DISAGREE");
                    }}
                />
            </div>
            )}

            {showSeriesNavigation && backToSeriesLabel ? (
                <ButtonGroup
                    className="discussion-series-navigation-controls max-w-full"
                    data-playwright-id="discussion-series-navigation-controls"
                >
                    {props.seriesNavigation?.previous?.href && previousInSeriesLabel ? (
                        <CtaButton
                            icon={ArrowLeft}
                            text={previousInSeriesLabel}
                            href={props.seriesNavigation.previous.href}
                            title={previousInSeriesTitle}
                            size="sm"
                            variant="outline"
                            extraClass="discussion-series-previous-button"
                            playwrightId="discussion-series-previous"
                            displayTextOnMobile={false}
                            roundedLeft
                        />
                    ) : null}

                    <CtaButton
                        icon={List}
                        text={backToSeriesLabel}
                        href={props.seriesNavigation!.backToSeriesHref}
                        title={backToSeriesTitle}
                        size="sm"
                        variant="outline"
                        extraClass="discussion-series-back-button"
                        playwrightId="discussion-series-back-link"
                        displayTextOnMobile={false}
                        roundedLeft={!props.seriesNavigation?.previous?.href}
                        roundedRight={!props.seriesNavigation?.next?.href}
                    />

                    {props.seriesNavigation?.next?.href && nextInSeriesLabel ? (
                        <CtaButton
                            icon={ArrowRight}
                            text={nextInSeriesLabel}
                            href={props.seriesNavigation.next.href}
                            title={nextInSeriesTitle}
                            size="sm"
                            variant="outline"
                            extraClass="discussion-series-next-button"
                            playwrightId="discussion-series-next"
                            displayTextOnMobile={false}
                            roundedRight
                        />
                    ) : null}
                </ButtonGroup>
            ) : null}

            {!isDeleted ? (
                <CtaButton
                    icon={Share}
                    variant="ghost"
                    size="sm"
                    requiresLogin={false}
                    displayTextOnMobile={false}
                    // `DISCUSSION-30` / `GROUP-17` / `SERIES-5` (wave-12 sheet Q1, option A). This row
                    // carries FIVE controls in the common case and SIX with series navigation, and at
                    // 412px that put the `…` on its own line in ~122px of empty footer. Share is the
                    // control that folds, because the menu beside it can hold it losslessly.
                    //
                    // STATIC CSS on both halves — this button hides below `sm`, the menu item at
                    // `actionItems` shows only below `sm`. No rendered branch and no `matchMedia`: a
                    // second source of truth about the viewport is how the two halves come to disagree,
                    // and the SSR'd card would have to pick one of them at render time.
                    //
                    // `hidden sm:flex`, not `sm:inline-flex`: `ctaBaseClass` is `flex`, so `inline-flex`
                    // would quietly change the control's box from `sm` up as well as its visibility.
                    extraClass={toned("discussion-share-button hidden sm:flex")}
                    playwrightId="discussion-share-button"
                    title={localizer("typescript.discussion.share")}
                    // `DISCUSSION-58` — the same pending signal the phone's bar gets. The gap this
                    // covers is not phone-only: the drawer chunk is fetched on desktop too, and that is
                    // where the fallback path is the DEFAULT (no `navigator.share`).
                    isLoading={sharePending}
                    onClick={(ev) => {
                        try {
                            ev?.preventDefault();
                            ev?.stopPropagation();
                        } catch {
                        }
                        void handleShare();
                    }}
                />
            ) : null}

            {/* `EMPTY-STATES-100` (wave-14 Q2) — the sub-tab's own verb, out of the `⋯` menu.

                Saved is stored as a Series, and the member was given exactly one unnamed, unorderable
                bucket over it: no way to split it, name a part of it, or promote a run of saved posts
                into a real series. The primitive that fixes that is the same primitive doing the
                storing, and the route into it was buried in the overflow on the one list whose whole
                job is organising a collection.

                Promoted, not duplicated — the menu item is withheld under the same predicate. It keeps
                its text at every width (`displayTextOnMobile` defaults on), unlike Share beside it:
                this is the row's ONE primary verb per Q2 and an unlabelled `ListPlus` beside an
                unlabelled bookmark is two icons a member has to guess between. Ghost, `sm`, so it sits
                in the same family as every other control in this footer rather than shouting over the
                card (design language rule: one hover wash, pill controls, no second emphasis). */}
            {!isDeleted && props.viewerSavedList ? (
                <CtaButton
                    icon={ListPlus}
                    variant="ghost"
                    size="sm"
                    requiresLogin={true}
                    accountAccessDialog={addToSeriesAccountAccessDialog}
                    extraClass={toned("discussion-add-to-series-button")}
                    playwrightId="discussion-add-to-series-button"
                    text={localizer("typescript.discussion.add_to_series")}
                    onClick={(ev) => {
                        try {
                            ev?.preventDefault();
                            ev?.stopPropagation();
                        } catch {
                        }
                        showAddDiscussionToSeriesDialog(props.discussionPublicId, props.localizer);
                    }}
                />
            ) : null}

            {/* Follow a post (question loop, Part A). The desktop half of ONE control; the phone half is the
                `…` menu's Follow / Stop following item (`sm:hidden`), exactly the Share split above, because
                one more pill on a phone is what pushes the `…` onto its own line. From `sm` up it is named by
                its visible label (WCAG 2.5.3, label in name); `title` carries the design's "Get notified about
                new replies" as the tooltip. `data-following` is the stable hook, written in BOTH states.
                Disabled until the first read lands, so a press can never act on a guess. */}
            {followEnabled ? (
                <CtaToggle
                    icon={Bell}
                    variant="ghost"
                    size="sm"
                    buttonActive={discussionFollow.following}
                    onPressedChange={() => void discussionFollow.toggle()}
                    disabled={!discussionFollow.known}
                    isLoading={discussionFollow.pending}
                    extraClass={toned("discussion-follow-toggle hidden sm:flex")}
                    playwrightId="discussion-follow-toggle"
                    data-following={discussionFollow.following ? "true" : "false"}
                    text={localizer(discussionFollow.following
                        ? "typescript.discussion.following"
                        : "typescript.discussion.follow")}
                    title={localizer("typescript.discussion.follow_title")}
                />
            ) : null}

            {!isDeleted && showViewerReactions ? (
                <CtaToggle
                    icon={Bookmark}
                    variant="ghost"
                    size="sm"
                    requiresLogin={true}
                    accountAccessDialog={saveAccountAccessDialog}
                    buttonActive={isSavedToBookmarks}
                    onPressedChange={() => void toggleBookmarksSave()}
                    disabled={savingToBookmarks}
                    isLoading={savingToBookmarks}
                    extraClass={toned("discussion-save-bookmarks-button")}
                     interactionEffect="bookmark"
                    playwrightId="discussion-save-bookmarks-button"
                    title={localizer(isSavedToBookmarks ? "typescript.discussion.saved" : "typescript.discussion.save")}
                    displayTextOnMobile={false}
                />
            ) : null}

            {/* `size="sm"`, like every sibling in this row. It was the one `md` (40px) control among
                `sm` (32px) ones, which made it the widest item and therefore the first to wrap — and
                the note above records that the `…` dropping onto its own line is precisely the defect
                this row is tuned against. The tap target is unaffected: getCtaClasses bakes the 44px
                coarse-pointer floor into `sm` and `md` alike, so only the painted box shrinks.
                Found by the 2026-08-03 cards refine run. */}
            {/* Withheld when it has nothing in it. Reachable for the first time as of 2026-08-14:
                "Copy link" used to be pushed unconditionally, so the menu was never empty; guarding it
                on `isDeleted` (EMPTY-STATES-24) means a non-author looking at a deleted post now
                produces zero action items and zero moderation items, and a `…` that opens onto nothing
                is worse than no `…`. */}
            {/* D2 (round 31 `3b`): with a host slot the menu is portaled out of this row, below. */}
            {moreMenu && !menuSlotRef && !rowLayout ? moreMenu : null}
        </div>
        {moreMenu && menuSlotElement ? createPortal(moreMenu, menuSlotElement) : null}

        {/* A door into the REAL composer, not a fake text field (design §8, §9). A native button, so
            its role, focusability and Enter/Space come from the platform. The composer dialog takes focus
            (`autoFocus`), which is what moves the reader's focus into it. */}
        {props.endOfPostAnchors?.replyRow && showEndOfPostReplyRow ? createPortal(
            <button
                type="button"
                className="standard-hover flex min-h-11 w-full touch-manipulation items-center gap-2 rounded-lg border border-control-boundary bg-input/70 px-3 py-2 text-left text-sm text-muted-foreground outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                data-playwright-id="discussion-end-reply-row"
                onClick={(ev) => {
                    try {
                        ev.preventDefault();
                        ev.stopPropagation();
                    } catch {
                    }
                    openArgumentEditor("end_of_post");
                }}
            >
                <MessageSquareText className="size-4 shrink-0" aria-hidden={true}/>
                {/* Round-7 D22 / C1: an anonymous reader is told that replying is how they join. The door
                    and what publishing requires are unchanged (SV-47 gates at Publish). It WRAPS rather than
                    truncating: the offer is carried by the second sentence, which a phone-width ellipsis cut
                    off in English and in every longer locale. */}
                <span className="min-w-0 break-words">
                    {localizer(MetaHandler.getLoggedInUsername()
                        ? "thought.editor.placeholder.paragraph"
                        : "discussion.end_reply_row.anonymous")}
                </span>
            </button>,
            props.endOfPostAnchors.replyRow,
        ) : null}

        {props.showFloatingMobileBar ? (
            <DiscussionMobileActionBar
                localizer={localizer}
                sectionNavigation={props.mobileSectionNavigation}
                showComment={showArgumentCountButton}
                argumentsCount={normalizedArgumentsCount}
                // `DISCUSSION-41` (wave-12 sheet Q4, option A, 2026-08-24). The bar's count button used to
                // wear the WRITE verb and open the composer, while the number on it was the thought count
                // — the largest, most prominent figure on the phone, with no word beside it and a tooltip
                // describing a click it had nothing to do with. It now goes to the section it counts, and
                // wears that unit's own existing name (`discussion.thoughts`; no new copy, rule 11).
                //
                // Only when the page supplied a section handler. Everywhere else there is nowhere to
                // navigate to, so the composer stays the click — and the write verb stays the label. The
                // two are read off ONE predicate for that reason: a label that can disagree with the
                // destination is the defect this row filed.
                //
                // The write path is not lost: the conversations panel this lands on carries its own
                // composer entry, and the disabled state still travels so a locked post is still stated.
                /*
                 * All three follow the DESTINATION, which is now always the composer — so the write
                 * guard applies again on mobile. While the button navigated, a write-disabled post
                 * still allowed the tap (there was nothing to write), and the label named the unit
                 * rather than the act. A label or an enabled state that can disagree with where the
                 * control actually goes is precisely what DISCUSSION-41 filed.
                 */
                commentDisabled={isWriteDisabled}
                commentDisabledReason={writeDisabledReason}
                commentTitle={localizer(writeThoughtLabelKey)}
                /*
                 * The right-most button opens the COMPOSER, on mobile and desktop alike.
                 *
                 * This re-points what `DISCUSSION-41` (2026-08-24) had sent to the conversations
                 * section. That change was made because the bar offered no route to the conversation
                 * at all and this control was the only thing that looked like one — but the SAME
                 * change added the sections dropdown (`discussion-mobile-bar-sections`), which is now
                 * that route and is a better one. With the read path covered, the count button is free
                 * to be the write path again, which is the thing a reader at the bottom of a thread on
                 * a phone most wants and otherwise has to scroll back up for.
                 */
                onComment={() => openArgumentEditor("discussion_mobile_bar")}
                agreementsCount={agreementsCount}
                disagreementsCount={disagreementsCount}
                isAgreeing={isAgreeing}
                isDisagreeing={isDisagreeing}
                votesDisabled={isDeleted || isTogglingAgreement}
                votesDisabledReason={isDeleted ? localizer("discussion.deleted.write_disabled") : null}
                agreementTitle={localizer("discussion.agreement")}
                disagreementTitle={localizer("discussion.disagreement")}
                reactAccountAccessDialog={reactAccountAccessDialog}
                onAgree={() => void toggleAgreement("AGREE")}
                onDisagree={() => void toggleAgreement("DISAGREE")}
                showShare={!isDeleted}
                onShare={() => void handleShare()}
                sharePending={sharePending}
                showBookmark={!isDeleted && showViewerReactions}
                isSavedToBookmarks={isSavedToBookmarks}
                savingToBookmarks={savingToBookmarks}
                saveAccountAccessDialog={saveAccountAccessDialog}
                onToggleBookmark={() => void toggleBookmarksSave()}
            />
        ) : null}

        {sendPost.pickerOpen ? <SendPostToNewsletterDialog state={sendPost} localizer={localizer}/> : null}

        {/* Mounted only once the author has opened it, so a page full of posts pays nothing for a
            dialog nobody touched. */}
        {reviewFacetDialogOpen ? (
            <ReviewFacetDialog
                localizer={localizer}
                open={reviewFacetDialogOpen}
                onOpenChange={setReviewFacetDialogOpen}
                discussionPublicId={props.discussionPublicId}
                initialSubjectPublicId={props.reviewSubjectPublicId}
                initialSubjectName={props.reviewSubjectName}
                initialRatingHalfStars={props.reviewRatingHalfStars}
                onSaved={(next) => props.onReviewFacetChanged?.(next)}
            />
        ) : null}
        </>
    );
}

